import React, { memo } from 'react';
import { CheckIcon, BookOpenIcon, PrinterIcon, LifeBuoyIcon, ShieldCheckIcon, ClockIcon, MessageCircleIcon, AwardIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function IndividualsPage() {
  return <div className="flex flex-col min-h-screen w-full" data-id="element-2263" data-mp="2k7p2mnvmp4ao">
      <TopBar data-id="element-2264" data-mp="2k99ycgtoita4" />
      <Header data-id="element-2265" data-mp="2k9jn612zmue8" />

      {/* Hero Section */}
      <section className="w-full bg-white py-20 md:py-28 border-b border-gray-200" data-id="element-2266" data-mp="2p58tqodfdbk1">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2267" data-mp="2u4tp6otdz1s0">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2268" data-mp="2u3aeddqi1ixm">
            <div className="inline-block bg-[#631bb7] bg-opacity-10 text-[#631bb7] px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-2269" data-mp="2z3eqkrkdjri1">
              For Individuals
            </div>
            <h1 className="mb-6 text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#111111] leading-tight" data-id="element-2270" data-mp="14pnbvdn6uhfq">
              You're Smarter Than This.
              <br data-id="element-2271" data-mp="ne95e3ypa1kq" />
              <span className="text-gray-500" data-id="element-2272" data-mp="ilg09ezbkr84">
                So Why Does Tech Make You Feel Stupid?
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto" data-id="element-2273" data-mp="dnegeru2knvl">
              You've built a career. Raised a family. Managed a mortgage. But
              every "update your password" notification feels like a pop quiz
              you didn't study for.{' '}
              <strong className="text-[#111111]" data-id="element-2274" data-mp="11cz2hjlis193">
                Caveo speaks plain English
              </strong>{' '}
              — no judgment, no jargon, just answers.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4" data-id="element-2275" data-mp="sc0y30765rpz">
              <Link to="/onboarding" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold no-underline hover:bg-[#521099] transition-all shadow-md" data-id="element-2276" data-mp="3h3bs557vq3w1">
                See What You'll Learn
              </Link>
            </div>
            <p className="text-gray-500" data-id="element-2277" data-mp="3ji9xrpa3qd6c">
              No credit card. Start with the basics.
            </p>
          </div>
        </div>
      </section>

      {/* Problem/Agitation Section */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24" data-id="element-2278" data-mp="39jany3dlobhr">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2279" data-mp="39lnf9758zy7h">
          <div className="max-w-3xl mx-auto" data-id="element-2280" data-mp="2vr4ibb82pmwk">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-8 text-center" data-id="element-2281" data-mp="2vqb8g1dtiha7">
              You Shouldn't Need a Computer Science Degree to Check Your Email
              Safely
            </h2>

            <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-2282" data-mp="2qt2nxu57615s">
              <p data-id="element-2283" data-mp="2qqymdiurte8o">
                <strong className="text-[#111111]" data-id="element-2284" data-mp="2cta1wc4m8945">
                  You're not technologically illiterate.
                </strong>{' '}
                You just didn't grow up with this stuff.
              </p>

              <p data-id="element-2285" data-mp="1y6ba2g1ujcow">
                But everywhere you turn, someone's talking about VPNs and
                firewalls and two-factor something-or-other like you're supposed
                to just <em data-id="element-2286" data-mp="1t9uljkqqiwba">know</em> what that means.
              </p>

              <p data-id="element-2287" data-mp="278uc4xxt5la3">
                Your bank sends you alerts about "suspicious activity" but
                doesn't explain what made it suspicious. Your email provider
                says "enable MFA" without defining MFA. YouTube tutorials assume
                you already understand the basics.
              </p>

              <p data-id="element-2288" data-mp="2c49oflg6394e">
                And the security companies? They're happy to sell you $15/month
                monitoring services while never actually teaching you what
                you're being monitored <em data-id="element-2289" data-mp="2lya0k79v9emm">for</em>.
              </p>

              <div className="bg-white rounded-2xl p-8 border-2 border-[#631bb7] border-opacity-30 my-8" data-id="element-2290" data-mp="2zv5diat8nq7y">
                <p className="text-xl text-[#111111] font-semibold text-center mb-0" data-id="element-2291" data-mp="2uxwsm2gph830">
                  Here's the truth: You don't need someone watching you.
                  <br data-id="element-2292" data-mp="3j0jxdmh5peuc" />
                  <span className="text-[#631bb7]" data-id="element-2293" data-mp="3j01q7usyhg7n">
                    You need someone to finally explain this in a way that makes
                    sense.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution/Benefit Section */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-2294" data-mp="2w1fdf31q5s0d">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2295" data-mp="300xnt4vx6koy">
          <div className="max-w-4xl mx-auto" data-id="element-2296" data-mp="303iohqkheul4">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4 text-center" data-id="element-2297" data-mp="34zqyyqnqhzly">
              Learn Digital Security Like a Normal Human
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12" data-id="element-2298" data-mp="katj7yfu6toa">
              Caveo isn't another monitoring app. We're the patient tech expert
              who actually takes the time to explain.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-2299" data-mp="29ryiumbttw6x">
              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-2300" data-mp="24sluty97yj2r">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-2301" data-mp="24uyd6ciw2vgc">
                  <MessageCircleIcon className="w-6 h-6 text-[#631bb7]" data-id="element-2302" data-mp="1v32aeo5uyh5s" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2303" data-mp="1r0xn07606grl">
                  Plain English, always
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2304" data-mp="1m12sltn0r91s">
                  No "multi-factor authentication protocols." Just "here's how
                  to add an extra password step, and here's why it matters."
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-2305" data-mp="3bh28dtl57wqe">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-2306" data-mp="3beoidz5blapp">
                  <ShieldCheckIcon className="w-6 h-6 text-[#631bb7]" data-id="element-2307" data-mp="14t1wc6ob7g8o" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2308" data-mp="19jie22z5xo0u">
                  Start where you are
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2309" data-mp="uvnhucly51j0">
                  Never touched a password manager? That's fine. We begin with
                  what threatens you most — password reuse, phishing emails,
                  backup basics — then build from there.
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-2310" data-mp="1sh1pted09kqy">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-2311" data-mp="1ehjv6id13d9o">
                  <CheckIcon className="w-6 h-6 text-[#631bb7]" data-id="element-2312" data-mp="1iilvz3mlfxdr" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2313" data-mp="32n4l4174g07">
                  Honest guidance
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2314" data-mp="72x7g7toyqau">
                  We'll tell you that most people don't need a VPN. We'll
                  explain why that antivirus software you bought at Best Buy is
                  probably fine. We're not here to sell you more stuff.
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-2315" data-mp="4kv68er8pssz">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-2316" data-mp="4ir1julioprn">
                  <ClockIcon className="w-6 h-6 text-[#631bb7]" data-id="element-2317" data-mp="381u6nzeznttm" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2318" data-mp="3mq89wbgbhkap">
                  Learn at your pace
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2319" data-mp="3hz8dr8f0v699">
                  No webinars to attend. No scheduled classes. Just 28 lessons
                  you can do whenever you have 10 minutes and a cup of coffee.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-id="element-2320" data-mp="23rrgr00fjjov">
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-2321" data-mp="28qkwkz36vms7">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-2322" data-mp="2dp5wsyify6gn">
                  Emotional Outcome
                </p>
                <p className="text-[#111111] font-medium" data-id="element-2323" data-mp="2hslrvqev3yr8">
                  <strong data-id="element-2324" data-mp="2mr5dfppjn8j2">Confidence.</strong> For the first time, you'll
                  understand <em data-id="element-2325" data-mp="2rox4khid6qm7">why</em> you're being asked to do these things
                  — and you'll know how to do them right.
                </p>
              </div>
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-2326" data-mp="2wi9r8m3hgncb">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-2327" data-mp="35gv9eg51m0ck">
                  Functional Outcome
                </p>
                <p className="text-[#111111] font-medium" data-id="element-2328" data-mp="3aghl2l5838j9">
                  <strong data-id="element-2329" data-mp="yt4ahg85nwj4">Stop feeling vulnerable.</strong> Stop clicking
                  "remind me later" on security prompts because you don't
                  understand them. Take control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof/Trust Section */}
      <section className="w-full bg-[#1a0647] py-16 md:py-24" data-id="element-2330" data-mp="ku4vl0ag2w05">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2331" data-mp="1hjm19f9fgi1u">
          <div className="max-w-4xl mx-auto" data-id="element-2332" data-mp="1hl6un1k09uy4">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center" data-id="element-2333" data-mp="1dh8viquokk52">
              Built by Someone Who Watched His Own Father Get Scammed
            </h2>

            <div className="bg-white bg-opacity-10 rounded-2xl p-8 mb-12" data-id="element-2334" data-mp="13rpfdsycr4dw">
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-2335" data-mp="13pl9j5gw7vaz">
                Jason Phillips founded Caveo after his dad lost money to cyber
                fraud — not because his dad was careless, but because nobody had
                ever explained the warning signs in language that made sense.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-2336" data-mp="3n073v5xp49gq">
                After 15 years at tech companies like Asana, Drift, and
                Quantcast, Jason saw the pattern:{' '}
                <strong className="text-[#00f4f8]" data-id="element-2337" data-mp="re54hc76pof">
                  The digital security industry profits from confusion.
                </strong>{' '}
                They'd rather sell you monitoring than teach you prevention.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed" data-id="element-2338" data-mp="9raz8yhcwgqv">
                So he built something different. A service designed for people
                who deserve clear answers, not more jargon.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2339" data-mp="xjrez2dmfwn0">
              <div className="bg-white rounded-2xl p-6" data-id="element-2340" data-mp="sjwo2ytuj71y">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-2341" data-mp="slg5isr8no5b">
                  "I'm 67 years old and I finally understand what two-factor
                  authentication does. Took Caveo 5 minutes to explain what my
                  grandkids couldn't in five years."
                </p>
                <p className="font-bold text-[#111111]" data-id="element-2342" data-mp="136tdukmyx81e">— Margaret K.</p>
                <p className="text-sm text-gray-500" data-id="element-2343" data-mp="1750rv7q1rv8w">Retired teacher</p>
              </div>
              <div className="bg-white rounded-2xl p-6" data-id="element-2344" data-mp="tdvz8pl3k382">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-2345" data-mp="y5nj6h9qzap8">
                  "No condescension. No eye-rolling. Just someone who takes the
                  time to teach. That's worth way more than another monitoring
                  app."
                </p>
                <p className="font-bold text-[#111111]" data-id="element-2346" data-mp="odrl367nvkl0">— Robert T.</p>
                <p className="text-sm text-gray-500" data-id="element-2347" data-mp="ahe0vpbqt52u">Age 58</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Breakdown */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-2348" data-mp="2h4lfj1vic8f3">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2349" data-mp="2cdv7thzlzth8">
          <div className="max-w-4xl mx-auto" data-id="element-2350" data-mp="2ccacvr9du8iu">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-12 text-center" data-id="element-2351" data-mp="27e0ktpiyfo1g">
              What You Get
            </h2>

            <div className="space-y-8" data-id="element-2352" data-mp="23ei0ovyyjgzo">
              <div className="flex items-start" data-id="element-2353" data-mp="3037kwsy24lot">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-2354" data-mp="301wbodluelqb">
                  <BookOpenIcon className="w-7 h-7 text-[#631bb7]" data-id="element-2355" data-mp="2v2a3iikvfvod" />
                </div>
                <div data-id="element-2356" data-mp="2r4m1ywoob4ni">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-2357" data-mp="2m3om4qr4ymp6">
                    28 Lessons Built for Adults
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-2358" data-mp="1fo44ct9cavza">
                    Not "tech tips" written for 20-year-olds. Real education
                    designed for people who have better things to do than
                    memorize technical terms. Each lesson: one skill, plain
                    English, 10 minutes or less.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-2359" data-mp="3vaq8b1xjc3ug">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-2360" data-mp="3xdfnalg8tmr">
                  <ShieldCheckIcon className="w-7 h-7 text-[#631bb7]" data-id="element-2361" data-mp="8vnebv6ducd4" />
                </div>
                <div data-id="element-2362" data-mp="duqildejgek1">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-2363" data-mp="du8ddr9wepzz">
                    Start With What Matters Most
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-2364" data-mp="mw7vkmwng04i">
                    We don't make you learn everything at once. Level 1 covers
                    the highest-risk vulnerabilities first (password hygiene,
                    two-factor, backups). You're protected before you're
                    perfect.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-2365" data-mp="1tfiolvi9rn1l">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-2366" data-mp="1pbazvybta73d">
                  <PrinterIcon className="w-7 h-7 text-[#631bb7]" data-id="element-2367" data-mp="239rf6dnqp5x4" />
                </div>
                <div data-id="element-2368" data-mp="1ydk6we7vfic0">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-2369" data-mp="1an6egmldjhgw">
                    Visual Guides You Can Print
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-2370" data-mp="15p4vs2rv1k5y">
                    Quick-reference cards for common tasks. No hunting through
                    videos. Just clear, step-by-step instructions you can keep
                    next to your computer.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-2371" data-mp="ruugsd5o14d3">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-2372" data-mp="my40pptv28ro">
                  <LifeBuoyIcon className="w-7 h-7 text-[#631bb7]" data-id="element-2373" data-mp="2yjeb5hw2ab9d" />
                </div>
                <div data-id="element-2374" data-mp="33cfqts43eeu0">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-2375" data-mp="33cz24pz8m4z2">
                    Crisis Response Plans
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-2376" data-mp="2tll3ekf4196z">
                    If something goes wrong, you'll know exactly what to do.
                    Step-by-step recovery plans for locked accounts, suspicious
                    charges, lost devices — everything.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24 border-t border-gray-200" data-id="element-2377" data-mp="3d8isyxyy2dgy">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-2378" data-mp="1wuk9nso8c7sy">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2379" data-mp="21uos3o8p7clt">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-2380" data-mp="21w9n1eyxcxk7">
              Stop Feeling Vulnerable.
              <br data-id="element-2381" data-mp="7gkntnvf4kqq" />
              Start Feeling Capable.
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-2382" data-mp="2gohhhz90h80">
              You don't need someone watching your accounts 24/7. You need
              someone to finally explain this stuff like you're an intelligent
              adult.
            </p>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 mb-8" data-id="element-2383" data-mp="3kt33ej9ih5tf">
              <p className="text-lg text-gray-600 mb-6" data-id="element-2384" data-mp="3fu9ruwdsbovw">
                For less than one coffee per month:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-md mx-auto mb-6" data-id="element-2385" data-mp="3auf1qv1u33bw">
                {['28 plain-English security lessons', 'Visual quick-reference guides', 'Crisis response plans', 'Honest guidance (no upsells)', 'Learn at your own pace'].map((item, index) => <div key={index} className="flex items-center" data-id="element-2386" data-mp="2mysgxq2b2wdt" data-mpi={index}>
                    <CheckIcon className="w-5 h-5 text-[#631bb7] mr-2 flex-shrink-0" data-id="element-2387" data-mp="2ryokxpb06ntd" />
                    <span className="text-[#111111]" data-id="element-2388" data-mp="2rwlnwlbfnuf5">{item}</span>
                  </div>)}
              </div>
              <div className="text-center mb-6" data-id="element-2389" data-mp="30rrtbj6skah5">
                <span className="text-4xl font-extrabold text-[#111111]" data-id="element-2390" data-mp="30qgorh5iuyoz">
                  $5.99
                </span>
                <span className="text-gray-500 text-lg" data-id="element-2391" data-mp="243c09d4h3iza">/month</span>
                <p className="text-gray-500 text-sm mt-1" data-id="element-2392" data-mp="290ly9h5xh2yq">
                  No contract. Cancel anytime.
                </p>
              </div>
              <Link to="/onboarding" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold inline-flex items-center justify-center no-underline hover:bg-[#521099] transition-all shadow-md w-full sm:w-auto" data-id="element-2393" data-mp="2i0ilrfgkzvs7">
                Start Learning Today
              </Link>
            </div>

            <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20 mb-6" data-id="element-2394" data-mp="35454yizcd5za">
              <div className="flex items-center justify-center mb-2" data-id="element-2395" data-mp="3063mo09qpaov">
                <AwardIcon className="w-5 h-5 text-[#631bb7] mr-2" data-id="element-2396" data-mp="3dwgxcae0n3hw" />
                <span className="font-bold text-[#111111]" data-id="element-2397" data-mp="394fcw16p26gs">
                  The Caveo Promise
                </span>
              </div>
              <p className="text-gray-600" data-id="element-2398" data-mp="3nsk1flsfhc7h">
                If you don't feel more confident in the first 30 days, we'll
                refund you immediately. No questions.
              </p>
            </div>

            <p className="text-gray-500 text-sm" data-id="element-2399" data-mp="ochatsuw88t">
              Join 2,000+ adults who decided they were done feeling stupid about
              technology.
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-2400" data-mp="22il8ub181ie0" />
    </div>;
}